import React, { useState } from "react";
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  AppBar,
  Toolbar,
  CssBaseline,
} from "@mui/material";
import { Dashboard, Person, ChevronRight } from "@mui/icons-material";
import { DashboardsPage } from "./DashboardsPage";
import { ProfilePage } from "./ProfilePage";
import { appConfig } from "../config/app-config";
import MetabaseDashboard from "./DashboardsPage copy";

interface HomePageProps {
  fontFamily?: string;
  onNavigateToEnGateway: () => void;
  onNavigateToIce: () => void;
}

const drawerWidth = 240;

export const HomePage: React.FC<HomePageProps> = ({
  fontFamily = "JetBrains Mono",
  onNavigateToEnGateway,
  onNavigateToIce,
}) => {
  const [selectedPage, setSelectedPage] = useState<"dashboards" | "profile">(
    "dashboards"
  );

  const menuItems = [
    {
      id: "dashboards",
      label: "Dashboards",
      icon: <Dashboard />,
      component: (
        <DashboardsPage
          fontFamily={fontFamily}
          onNavigateToEnGateway={onNavigateToEnGateway}
          onNavigateToIce={onNavigateToIce}
        />
      ),
    },
    {
      id: "profile",
      label: "Profile",
      icon: <Person />,
      component: <ProfilePage fontFamily={fontFamily} />,
    },
    {
      id: "metabaseDashboards",
      label: "Metabase Dashboards",
      icon: <Dashboard />,
      component: <MetabaseDashboard />,
    },
  ];

  const handleMenuClick = (pageId: "dashboards" | "profile") => {
    setSelectedPage(pageId);
  };

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />

      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
      >
        <Toolbar>
          <Typography variant="h6" noWrap component="div" sx={{ fontFamily }}>
            {appConfig.dashboardTitle}
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Sidebar Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: drawerWidth,
            boxSizing: "border-box",
          },
        }}
      >
        <Toolbar />
        <Box sx={{ overflow: "auto", mt: 2 }}>
          <List>
            {menuItems.map((item) => (
              <ListItem key={item.id} disablePadding>
                <ListItemButton
                  selected={selectedPage === item.id}
                  onClick={() =>
                    handleMenuClick(item.id as "dashboards" | "profile")
                  }
                  sx={{
                    fontFamily,
                    "&.Mui-selected": {
                      backgroundColor: "primary.main",
                      color: "white",
                      "&:hover": {
                        backgroundColor: "primary.dark",
                      },
                      "& .MuiListItemIcon-root": {
                        color: "white",
                      },
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: selectedPage === item.id ? "white" : "inherit",
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{ fontFamily }}
                  />
                  {selectedPage === item.id && (
                    <ChevronRight sx={{ color: "white" }} />
                  )}
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>

      {/* Main Content */}
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Toolbar />
        {menuItems.find((item) => item.id === selectedPage)?.component}
      </Box>
    </Box>
  );
};
